const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const normalizeMode = (mode) => (mode === 'stack' ? 'stack' : 'override');

const normalizeServiceKey = (key) => (key || '').toString().trim().toLowerCase();

function resolveNegotiatedRate(serviceTypeKey, negotiatedRateSchedule) {
    const normalizedKey = normalizeServiceKey(serviceTypeKey);
    if (!normalizedKey) return null;

    if (Array.isArray(negotiatedRateSchedule)) {
        const matched = negotiatedRateSchedule.find((row) =>
            normalizeServiceKey(row?.service_type_key) === normalizedKey
        );
        return matched ? Number(matched.negotiated_rate) : null;
    }

    if (negotiatedRateSchedule && typeof negotiatedRateSchedule === 'object') {
        const key = Object.keys(negotiatedRateSchedule).find(
            (candidate) => normalizeServiceKey(candidate) === normalizedKey
        );
        return key ? Number(negotiatedRateSchedule[key]) : null;
    }

    return null;
}

export function resolveInvoiceLinePricing({
    serviceTypeKey,
    baseRate,
    quantity = 1,
    tierDiscountPct = 0,
    negotiatedRateSchedule = null,
    discountResolutionMode = 'override'
}) {
    const mode = normalizeMode(discountResolutionMode);
    const qty = Number(quantity) > 0 ? Number(quantity) : 1;
    const baseUnitRate = Number(baseRate) || 0;
    const negotiatedUnitRate = resolveNegotiatedRate(serviceTypeKey, negotiatedRateSchedule);
    const negotiatedIsUsable =
        Number.isFinite(negotiatedUnitRate) &&
        negotiatedUnitRate >= 0 &&
        negotiatedUnitRate < baseUnitRate;
    const tierPct = Math.max(0, Number(tierDiscountPct) || 0);

    const effectiveUnitRate = negotiatedIsUsable ? Math.min(baseUnitRate, negotiatedUnitRate) : baseUnitRate;
    const negotiatedDiscountUnit = negotiatedIsUsable ? Math.max(0, baseUnitRate - effectiveUnitRate) : 0;
    const tierBaseUnit =
        mode === 'stack'
            ? effectiveUnitRate
            : (negotiatedIsUsable ? 0 : baseUnitRate);
    const tierDiscountUnit = roundMoney((tierBaseUnit * tierPct) / 100);

    const negotiatedDiscount = roundMoney(negotiatedDiscountUnit * qty);
    const tierDiscount = roundMoney(tierDiscountUnit * qty);
    const gross = roundMoney(baseUnitRate * qty);
    const totalDiscount = roundMoney(negotiatedDiscount + tierDiscount);
    const net = roundMoney(gross - totalDiscount);

    const discountLines = [];
    if (negotiatedDiscount > 0) {
        discountLines.push({
            code: 'negotiated_rate',
            label: `Negotiated rate (${serviceTypeKey || 'service'})`,
            amount: -negotiatedDiscount
        });
    }
    if (tierDiscount > 0) {
        discountLines.push({
            code: 'tier_discount',
            label: `Tier discount (${tierPct}%)`,
            amount: -tierDiscount
        });
    }

    return {
        gross,
        net,
        totalDiscount,
        effectiveUnitRate: roundMoney(effectiveUnitRate),
        discountLines
    };
}

export function buildInvoicePricingSummary({
    lineItems,
    tierDiscountPct = 0,
    negotiatedRateSchedule = null,
    discountResolutionMode = 'override'
}) {
    const normalizedLineItems = Array.isArray(lineItems) ? lineItems : [];
    const resolvedLineItems = normalizedLineItems.map((line) => {
        const pricing = resolveInvoiceLinePricing({
            serviceTypeKey: line.service_type_key || line.serviceTypeKey || line.description,
            baseRate: line.base_rate ?? line.unit_price ?? 0,
            quantity: line.quantity ?? 1,
            tierDiscountPct,
            negotiatedRateSchedule,
            discountResolutionMode
        });

        return {
            ...line,
            gross: pricing.gross,
            discount_lines: pricing.discountLines,
            total_discount: pricing.totalDiscount,
            total: pricing.net
        };
    });

    const aggregate = resolvedLineItems.reduce((acc, line) => {
        const lineGross = Number(line.gross) || 0;
        const lineDiscount = Number(line.total_discount) || 0;
        acc.subtotal += roundMoney(lineGross);
        acc.totalDiscount += roundMoney(lineDiscount);
        (line.discount_lines || []).forEach((discountLine) => {
            const key = discountLine.code;
            if (!acc.byCode[key]) {
                acc.byCode[key] = { code: key, label: discountLine.label, amount: 0 };
            }
            acc.byCode[key].amount = roundMoney(acc.byCode[key].amount + (Number(discountLine.amount) || 0));
        });
        return acc;
    }, { subtotal: 0, totalDiscount: 0, byCode: {} });

    return {
        lineItems: resolvedLineItems,
        discountLines: Object.values(aggregate.byCode),
        subtotal: roundMoney(aggregate.subtotal),
        totalDiscount: roundMoney(aggregate.totalDiscount),
        total: roundMoney(aggregate.subtotal - aggregate.totalDiscount)
    };
}
